/**
 * Official UAE Dirham *display* sign (Unicode 18.0+): U+20C3 UAE DIRHAM SIGN.
 * ISO 4217 code remains AED; this is presentation only.
 *
 * Wave 3: thin compatibility wrappers around `@/utils/format` UAE / print profiles.
 * Prefer `formatMoney` / `formatPrintAmount` for new code.
 * @see Unicode Standard — UAE DIRHAM SIGN
 */
import {
  formatMoney,
  UAE_MONEY_PROFILE,
  CURRENCY_NBSP,
} from '@/utils/format';

export const AED_CURRENCY_CODE = 'AED' as const;

/** The official UAE dirham sign character. May render as missing glyph without font support; use AedDirhamSign in UI when needed. */
export const AED_DISPLAY_SIGN_CHAR = '\u20C3';

/** Spacing between sign and amount for AED. */
export const CURRENCY_DISPLAY_NBSP = CURRENCY_NBSP;

export function isLegacyAedSymbol(s: string | undefined | null): boolean {
  if (s == null) return false;
  return s === 'د.إ' || s.includes('د');
}

/** Normalize stored or legacy symbols to the official sign for AED. */
export function resolveAedDisplaySymbol(storedSymbol: string | undefined | null): string {
  if (storedSymbol == null || storedSymbol === '') {
    return AED_DISPLAY_SIGN_CHAR;
  }
  if (isLegacyAedSymbol(storedSymbol) || storedSymbol === AED_CURRENCY_CODE) {
    return AED_DISPLAY_SIGN_CHAR;
  }
  return storedSymbol;
}

/**
 * For chart tooltips, table prefixes, and other plain text where U+20C3 is not in the font (renders as ▯).
 * Returns ISO `AED` for dirham; otherwise the stored symbol or currency code.
 */
export function uiPlainCurrencyLabel(
  symbol: string | undefined | null,
  currencyCode: string | undefined | null
): string {
  const code = (currencyCode || '').toUpperCase();
  const sym = symbol ?? '';
  if (code === AED_CURRENCY_CODE || sym === AED_DISPLAY_SIGN_CHAR || isLegacyAedSymbol(sym)) {
    return AED_CURRENCY_CODE;
  }
  if (sym && sym !== AED_DISPLAY_SIGN_CHAR) return sym;
  return code;
}

/**
 * Format a number with a currency sign. For **plain text** output, AED cannot safely use U+20C3 — most
 * UI/body fonts lack the glyph (renders as a box). This returns `AED` + NBSP + amount. Use
 * `AedDirhamSign` + a numeric part in React when you need the official mark.
 *
 * @deprecated Prefer `formatMoney(amount, { ...UAE_MONEY_PROFILE, currency })` for new call sites.
 */
export function formatWithCurrencySymbol(
  amount: number,
  symbol: string,
  currencyCode?: string,
  options?: Intl.NumberFormatOptions
): string {
  const code = (currencyCode || '').toUpperCase();
  const isAed =
    code === AED_CURRENCY_CODE ||
    symbol === AED_DISPLAY_SIGN_CHAR ||
    isLegacyAedSymbol(symbol);
  if (isAed || !symbol) {
    return formatMoney(amount, {
      ...UAE_MONEY_PROFILE,
      currency: isAed ? AED_CURRENCY_CODE : code || 'AED',
      display: isAed || !symbol ? (symbol ? 'code_prefix' : 'plain') : 'symbol_prefix',
      symbol,
      minimumFractionDigits: options?.minimumFractionDigits as number | undefined,
      maximumFractionDigits: options?.maximumFractionDigits as number | undefined,
    });
  }
  return formatMoney(amount, {
    currency: code || AED_CURRENCY_CODE,
    locale: 'en-US',
    display: 'symbol_prefix',
    symbol,
    minimumFractionDigits: (options?.minimumFractionDigits as number | undefined) ?? 2,
    maximumFractionDigits: (options?.maximumFractionDigits as number | undefined) ?? 2,
  });
}

/**
 * For CSV/Excel: combined string with official sign, UTF-8 safe.
 * Set preferIso to use "AED 1,234.56" when the consumer cannot show U+20C3.
 */
export function formatAedForExportString(amount: number, preferIsoInsteadOfSign = false): string {
  if (preferIsoInsteadOfSign) {
    return formatMoney(amount, {
      ...UAE_MONEY_PROFILE,
      separator: ' ',
    });
  }
  const n = formatMoney(amount, { ...UAE_MONEY_PROFILE, display: 'plain' });
  return `${AED_DISPLAY_SIGN_CHAR}${CURRENCY_DISPLAY_NBSP}${n}`;
}

/**
 * For print templates: format amount with correct sign when `currencyCode` is AED. Pass optional DB symbol for legacy rows.
 */
export function formatPrintAmount(
  amount: number,
  currencyCode: string,
  symbolFromRow?: string
): string {
  const code = (currencyCode || AED_CURRENCY_CODE).toUpperCase();
  if (code === 'AED') {
    return formatMoney(amount, UAE_MONEY_PROFILE);
  }
  if (symbolFromRow) {
    return formatWithCurrencySymbol(amount, symbolFromRow, code);
  }
  return formatMoney(amount, {
    currency: code,
    locale: 'en-US',
    display: 'code_prefix',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    separator: ' ',
  });
}
